import { isEmpty } from 'lodash'
import { getLiveConfigurationCache } from './configuration/configurationCache'
import { shouldPresentBFeature } from './featureCohortTesting'

const WAVEFORM_BAR_COLOR = 'rgb:282636'
const WAVEFORM_BACKGROUND_COLOR = 'transparent'
const WAVEFORM_WIDTH = '700'
const WAVEFORM_HEIGHT = '200'

const cloudinaryCloudName = process.env.REACT_APP_CLOUDINARY_CLOUDNAME
const mediaServiceBaseUrl = process.env.REACT_APP_MEDIASERVICE_BASE_URL

const cloudinaryUserAssetUrl = process.env.REACT_APP_ENV === 'production' ?
  'https://media.plickers.com/' : 'https://media.plickers-dev.com/'

const mediaServiceUserAssetUrl = `${mediaServiceBaseUrl}/${cloudinaryCloudName}/`

function debug(...args) {
  if (process.env.REACT_APP_ENV !== 'production') {
    console.log(...args)
  }
}

export function shouldPresentMediaService() {
  const userId = JSON.parse(localStorage.getItem('ls.session'))?.user
  const liveConfig = getLiveConfigurationCache() || {}
  const {
    mediaServiceUseProbability,
  } = liveConfig

  // if default is 100, always use 100. otherwise use the configuration value if available
  const probability = process.env.REACT_APP_MEDIASERVICE_DEFAULT_USE_PROBABILITY === '100' ?
    '100' :
    mediaServiceUseProbability ?? process.env.REACT_APP_MEDIASERVICE_DEFAULT_USE_PROBABILITY
  return shouldPresentBFeature('mediaService', userId, probability)
}

function getUserAssetUrl() {
  const shouldUseMediaService = shouldPresentMediaService()
  debug('choosing:', shouldUseMediaService ? 'mediaServiceUserAssetUrl' : 'cloudinaryUserAssetUrl')
  return shouldUseMediaService ? mediaServiceUserAssetUrl : cloudinaryUserAssetUrl
}

function collapseMultipleSlashes(str) {
  return str.replace(/(^\w+:\/\/)|\/{2,}/g, (_, protocol) => (protocol || '/'))
}

function getMediaBaseUrl(baseUrl, options = {}) {
  const optionsToUse = { resource: 'image', delivery: 'upload', ...options }
  const resourceAndDelivery = `${optionsToUse.resource}/${optionsToUse.delivery}/`
  return collapseMultipleSlashes(`${baseUrl}${resourceAndDelivery}/`)
}

function composeDeliveryUrl(baseUrl, publicId, options = {}) {
  const transformationString = isEmpty(options.transformations) ? '' : `${options.transformations}/`
  const versionString = options.version ? `${options.version}/` : ''
  const folderPathString = options.folderPath ? `${options.folderPath}/` : ''
  const formatString = options.format ? `.${options.format}` : ''
  return collapseMultipleSlashes(`${getMediaBaseUrl(baseUrl, options)}${transformationString}${versionString}${folderPathString}${publicId}${formatString}`)
}

function getGifUrls(publicId, options = {}) {
  return {
    stillUrl: composeDeliveryUrl(getUserAssetUrl(), publicId, { ...options, transformations: 'pg_1' }),
    webpUrl: composeDeliveryUrl(getUserAssetUrl(), publicId, { ...options, format: 'webp' }),
    gifUrl: composeDeliveryUrl(getUserAssetUrl(), publicId, { ...options, format: 'gif' }),
  }
}

// used by calculateImageDeliveryValues.js to get the base url for the image
// we should be returning cloudinary url here until we completely migrate to media service
function getImageBaseUrl() {
  return getMediaBaseUrl(cloudinaryUserAssetUrl)
  // return getMediaBaseUrl(getUserAssetUrl())
}

function getImageUrl(publicId, options = {}) {
  return composeDeliveryUrl(getUserAssetUrl(), publicId, options)
}

function conformDeliveryUrlToMediaService(deliveryUrl) {
  const shouldUseMediaService = shouldPresentMediaService()
  if (shouldUseMediaService) {
    if (!deliveryUrl || !deliveryUrl.includes(cloudinaryUserAssetUrl)) {
      debug('not rewriting - not a cloudinary url', deliveryUrl)
      return deliveryUrl
    }
    const newDeliveryUrl = deliveryUrl.replace(cloudinaryUserAssetUrl, mediaServiceUserAssetUrl)
    debug('deliveryUrl', deliveryUrl, 'newDeliveryUrl', newDeliveryUrl)
    return newDeliveryUrl
  }
  debug('media service delivery rewrite or media proxy rewrite not required', deliveryUrl)
  return deliveryUrl
}

function conformDeliveryUrlToCloudinary(deliveryUrl) {
  if (!deliveryUrl || !deliveryUrl.includes(mediaServiceUserAssetUrl)) {
    debug('not mutating back, not a media service url', deliveryUrl)
    return deliveryUrl
  }
  const newDeliveryUrl = deliveryUrl.replace(mediaServiceUserAssetUrl, cloudinaryUserAssetUrl)
  debug('mutating back to cloudinary', deliveryUrl, 'newDeliveryUrl', newDeliveryUrl)
  return newDeliveryUrl
}

function getSoundDeliveryUrl(publicId, start = null, end = null, isClipped = false, withFormat = true) {
  const transformations = isClipped ? `so_${start},eo_${end}/` : ''
  return composeDeliveryUrl(getUserAssetUrl(), publicId, { resource: 'video', transformations, format: withFormat ? 'mp3' : null })
}

function getSoundWaveformUrl(publicId, start = null, end = null, isClipped = false) {
  const clipString = isClipped ? `so_${start},eo_${end}/` : ''
  const transformations = `${clipString}h_${WAVEFORM_HEIGHT},w_${WAVEFORM_WIDTH},fl_waveform,co_${WAVEFORM_BAR_COLOR},b_${WAVEFORM_BACKGROUND_COLOR}/`
  return composeDeliveryUrl(getUserAssetUrl(), publicId, { resource: 'video', transformations, format: 'png' }) // use png for Safari support
}

export {
  getImageUrl,
  getSoundDeliveryUrl,
  getSoundWaveformUrl,
  getGifUrls,
  getImageBaseUrl,
  conformDeliveryUrlToMediaService as conformDeliveryUrl,
  conformDeliveryUrlToCloudinary as keepDeliveryUrlCloudinary,
}
